import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess'
import _ from 'lodash'
import { createContext, Suspense, useContext, useEffect, useReducer, useRef, useState } from 'react'
import { Segment, Sidebar } from 'semantic-ui-react'
import { getDataManagementOptions, getDataSidebarOptions } from '../../auth/actionAccessPermissions'
import DataProvider, { DataContext } from '../../cnr/contexts/DataContext'
import { FirestoreContext } from '../../cnr/contexts/FirestoreContext'
import { FrameworkContext } from '../../cnr/contexts/FrameworkContent'
import { GoogleSheetsContext } from '../../cnr/contexts/GoogleSheetsContext'
import { ParentContext } from '../../cnr/contexts/ParentContext'
import { dataManagementHandlers, dataManagementInitialState, dataManagementReducer, viewDataModes } from '../../cnr/reducers/DataManagmentReducer'
import { helpTypes } from '../../cnr/reducers/HelpReducer'
import { getGoogleSheetDataValues } from '../../common/convert'
import UiSaveButtons from '../../components/buttons/UiSaveButtons'
import { clickOriginTypes } from '../../enums/globalEnums'
import AppDataContent from '../../pageItem/modification/AppDataContent'
import UiDataModifications, { dataModificationDisplayTypes } from '../../pageItem/modification/UiDataModifications'
import SettingsViewer from '../../viewSettings/actions/SettingsViewer'
import { appDataActionTypes } from '../../viewSettings/enums/itemActionTypes'
import { openExternal } from '../../viewSettings/helpers/settingsLinks'
import SettingsFormEdit from "../../viewSettings/subComponents/SettingsFormEdit"
import FullPageWrapper from '../../wrappers/FullPageWrapper'
import TransitionWrapper, { transitionWrapperTypes } from '../../wrappers/TransitionWrapper'
import Wrapper, { wrapperTypes } from '../../wrappers/Wrapper'
import SuspenseDimmer from '../alerts/SuspenseDimmer'
import { getHeaders } from '../headers/getHeaders'
import Help from '../help/Help'
import MenuVert from '../menus/MenuVert'
import DataSidebar from '../swipers/DataSidebar'
import AlphaTableViewer from "./AlphaTableViewer"
import { appViewerColors } from './AppDashboard'
import DataManagementTop from './DataManagementTop'
import JsonViewer from "./JsonViewer"
import TableViewer from "./TableViewer"

export const DataManagementContext = createContext();

const _alphaLimit = 30
const _defaultMenuItem = 'collectionOptions'

/**
 * 
 * @param {object} props (clickOriginType, uivi, viewItem, viewListData)
 * @returns 
 */
const DataManagementViewer = (props) => {

  const fromAppDocuments = true

  const {
    clickOriginType,
    uivi: uivi_direct,
    viewItem: viewItem_direct,
    viewListData: viewListData_direct
  } = props ?? {}

  let _fromDataViewer;

  switch (clickOriginType) {
    case clickOriginTypes.dataViewer:
      _fromDataViewer = true
    default:
    //nothing
  }

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns, settings, fn_cls } = parentContext ?? {}
  const { app_state, paps_state, currentSettings_state, eventInfo_state } = states ?? {}
  const { appSettings_fns, page_fns, database_fns } = fns ?? {}
  const { mainDb } = app_state ?? {}
  const { pathViews, viewKey } = paps_state ?? {}
  const appUserAccess = useAppUserAccess()
  const { isSuperAdminDeveloper } = appUserAccess ?? {}

  // homeSettingsContext 
  const { homeSettings } = settings ?? {}
  const { global } = homeSettings ?? {}
  const { permissionsConsole: permissionsConsole_home_global } = global ?? {}
  const { dataManagementSettings } = permissionsConsole_home_global ?? {}

  // eventInfoContext  
  const { staticViews: staticViews_app, appDataDocuments } = eventInfo_state ?? {}


  const { aps_global, aps_viewItems, aps_views } = _.get(currentSettings_state, 'gvviSettings', {});

  const { appDataManagement, dataOptions, productionSettings, googleSheets, dataRestrictions } = aps_global ?? {}
  const { dateFormat } = dataOptions ?? {}
  const { googleSheetsId } = googleSheets ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const { fullMode } = framework_state ?? {}

  // firestoreContext
  const firestoreContext = useContext(FirestoreContext);
  const { firestore_handlers } = firestoreContext ?? {}

  // googleSheetsContext
  const googleSheetsContext = useContext(GoogleSheetsContext)
  const { googleSheets_state } = googleSheetsContext ?? {}
  const { updating, confirmation } = googleSheets_state ?? {}

  // dataContext
  const dataContext = useContext(DataContext);
  const { data_state } = dataContext ?? {}
  const { uivi, viewItem, viewListData, fsr } = data_state ?? {}
  const { dataSource } = viewItem ?? {}
  const { altDataCollectionName } = dataSource ?? {}

  let _uivi = altDataCollectionName ? altDataCollectionName : uivi
  if (uivi_direct) { _uivi = uivi_direct }

  let _viewItem = viewItem_direct ? viewItem_direct : viewItem
  if (!_viewItem) { _viewItem = {} }
  const _viewListData = viewListData_direct ? viewListData_direct : viewListData
  const { key: key_viewItem } = _viewItem ?? {}

  const init_state = { fn_cls, mainDb, appSettings_fns, database_fns, fsr, dataRestrictions, productionSettings, firestore_handlers, page_fns, _defaultMenuItem, dataManagementType: _defaultMenuItem, viewListData: _viewListData, staticViews_app, dateFormat, pathViews, viewKey, viewItem: _viewItem, aps_viewItems, clickOriginType }
  const [dataManagement_state, fullData_dispatch] = useReducer(dataManagementReducer, dataManagementInitialState(init_state));
  const dataManagement_handlers = dataManagementHandlers(fullData_dispatch)
  const { handleDataActionSelect } = dataManagement_handlers ?? {}

  const {
    // activeTab, // triggered from DataTabs
    appData,
    dataCount,
    fixedData,
    dataManagementType,
    selectedItem,
    showExport,
    showOptions,
    showHelp,
    tabData,
    viewDataMode,
    viewItem_previewTemp,
  } = dataManagement_state ?? {}

  const _appData = fromAppDocuments && appDataDocuments ? appDataDocuments[uivi_direct] : appData

  const [dataActionOptions, setDataActionOptions] = useState()
  const [showAlpha, setShowAlpha] = useState()
  const [dataManagementOptions, setDataManagementOptions] = useState()
  const [dataSidebarOptions, setDataSidebarOptions] = useState()

  const tableRef = useRef()

  useEffect(() => {
    let _dataManagementOptions = getDataManagementOptions(dataManagementSettings, appDataManagement, appUserAccess)
    let _dataSidebarOptions = getDataSidebarOptions(dataManagementSettings, appDataManagement, appUserAccess)
    setDataManagementOptions(_dataManagementOptions)
    setDataSidebarOptions(_dataSidebarOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_uivi, _viewListData, viewItem]);


  useEffect(() => {
    if (selectedItem) {
      console.log('selectedItem', selectedItem)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    if (_viewItem) {
      const ada = []
      if (dataManagementOptions) {
        _.forEach(dataManagementOptions, (dmo, dmoKey) => {
          switch (dmoKey) {
            case 'manageData':
            case 'viewData':
              break;
            default:
              if (dmo.allowAction) {
                ada.push(dmoKey)
              }
          }
        })
      }
      setDataActionOptions(ada)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataManagementOptions, _viewListData]);

  useEffect(() => {
    if (_viewListData) {
      dataManagement_handlers.handleAppItemData(_viewItem, _viewListData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_uivi, _viewListData]);

  useEffect(() => {
    if (_appData) {
      const dataCount = _appData ? Object.keys(_appData).length : 0
      if (dataCount > _alphaLimit) {
        setShowAlpha(true)
      } else {
        setShowAlpha(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_appData]);

  useEffect(() => {
    if (fixedData) {
      dataManagement_handlers.handleActiveTab(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedData]);

  const [showDataOptions, setShowDataOptions] = useState()
  const [showDataActions, setShowDataActions] = useState()

  // shortcuts for dataManagement_handlers
  const handleDatabaseOpen = () => openExternal.db(pathViews, null, [_uivi])
  const handleOpenGoogleSheets = () => openExternal.googleSheets(googleSheetsId)
  const handlePreview = (data) => dataManagement_handlers.handleSettingsFormUpdate(data)
  const handleRowSelect = (item) => dataManagement_handlers.handleRowSelect(item) // changes viewDataMode
  const handleSettingsFormUpdate = (data, settingsType, save) => dataManagement_handlers.handleSettingsFormUpdate(data, settingsType, save)
  const handleShow_dataOptions = () => setShowDataOptions(!showDataOptions)
  const handleShow_dataActions = () => setShowDataActions(!showDataActions)
  const handleShow_help = () => dataManagement_handlers.handleShowHelp()

  const handleViewDataMode = (dsoKey) => {
    const opts = { viewDataMode: dsoKey }
    setShowDataActions(false)
    dataManagement_handlers.handleViewDataMode(opts)
  }

  const handleShowExport = (opts) => dataManagement_handlers.handleShowExport()


  const handleSelectActionOption = (key) => {
    setShowDataOptions()
    dataManagement_handlers.handleSelect_dataActionOption(key)
  }

  /**
   * 
   * @returns a menu with all of the dataActionOptions
   * @description onClick will triggner handleSelectActionOption
   */
  const dataManagementMenu = () => <TransitionWrapper>
    <MenuVert
      menuKey={'ms_sss'}
      menuItems={dataActionOptions}
      oc={handleSelectActionOption}
      header={'Collection Options'}
    ></MenuVert>
  </TransitionWrapper>

  const dataSidebarMenu = () => <TransitionWrapper>
    <MenuVert
      menuKey={'ms_sss'}
      menuItems={_.keys(dataSidebarOptions)}
      oc={handleViewDataMode}
      header={'Data Options!!'}
    ></MenuVert>
  </TransitionWrapper>

  const handleExportToGoogle = () => {
    const { headers } = getHeaders(_appData)
    const { values, tabName } = getGoogleSheetDataValues(Object.values(_appData), headers, _.startCase(uivi))
    fn_cls.updateGoogleSheetValues(googleSheetsId, tabName, values)
  }

  const footer_export = () => {
    const tabName = _.startCase(uivi)
    const btns = [
      { caption: _.startCase('exportToGoogleSheets') + ' (' + tabName + ')', oc: handleExportToGoogle, icon: 'save' },
    ]
    return <UiSaveButtons
      others={btns}
      color={'green'}
    />
  }

  /**
   * 
   * @returns an editable form
   */
  const settingsForm = () => <SettingsFormEdit
    settingsType={viewDataMode}
    formData={selectedItem}
    useWrapper={true}
    previewForm={handlePreview}
    updateForm={viewItem_previewTemp && handleSettingsFormUpdate}
    closeForm={handleViewDataMode}
    isGlobal={false}
    inSidebar={true}
  />

  /**
   * 
   * @returns the content for the sidebar based on the `viewDataMode`
   * @description this will only be shown if `viewDataMode` is set.
   */
  const rightSidebarContent = () => {
    if (appDataActionTypes[viewDataMode]) {
      return <AppDataContent />
    } else {
      switch (viewDataMode) {
        case viewDataModes.dataSource:
          return settingsForm()
        case viewDataModes.viewJson:
          return <JsonViewer json={tabData} name={viewDataMode} />
        case viewDataModes.dataEdit:
          return <UiDataModifications dataModificationDisplayType={dataModificationDisplayTypes.direct} clickOriginType={clickOriginType} />
        case viewDataModes.fixSettings:
          const projectSettings = appSettings_fns.cleanUpSettings(_uivi, aps_views, aps_viewItems)
          return <SettingsViewer createInfo={{ projectSettings }} />
        default:
          return <div>No View Data Mode</div>
      }
    }
  }

  const wrapper_sidebarRight = () => <Suspense fallback={<SuspenseDimmer origin={'DataManagementViewer'} />}>
    <Wrapper
      header={_.startCase(viewDataMode)}
      content={rightSidebarContent()}
      wrapperType={wrapperTypes.paddedHeader}
      css={{ container: 'data-sidebar' }}
    />
  </Suspense>

  const wrapper_exportRight = () => <Suspense fallback={<SuspenseDimmer origin={'DataManagementViewer'} />}>
    <Wrapper
      header={_.startCase('exportToGoogleSheets') + ': ' + googleSheetsId}
      content={<JsonViewer json={_appData} />}
      footer={footer_export()}
      wrapperType={wrapperTypes.paddedHeader}
      css={{ container: 'data-sidebar' }}
    />
  </Suspense>

  const wrapper_options = () => <Suspense fallback={<SuspenseDimmer origin={'DataManagementViewer'} />}>
    <Wrapper
      header={_.startCase('exportToGoogleSheets') + ': ' + googleSheetsId}
      content={<JsonViewer json={_appData} />}
      footer={footer_export()}
      wrapperType={wrapperTypes.paddedHeader}
      css={{ container: 'data-sidebar' }}
    />
  </Suspense>

  /** The button that triggers the dataOptions to be shown */
  const dataOptionsButton_top = () => {
    const btns = [
      { caption: _.startCase(dataManagementType), oc: handleShow_dataOptions, icon: 'unordered list' },
    ]
    return <UiSaveButtons
      others={btns}
      color={appViewerColors.dmv}
    />
  }

  const dataOptionsButton_bottom = () => {
    const btns = []
    // these are the options at the bottom
    btns.push({ caption: _.startCase('dataOptions'), oc: handleShow_dataActions, icon: 'unordered list', fl: true })
    isSuperAdminDeveloper && btns.push({ caption: _.startCase('openDatabase'), oc: handleDatabaseOpen, icon: 'database', fl: true, color: 'orange' })
    btns.push({ caption: _.startCase('openGoogleSheets'), oc: handleOpenGoogleSheets, icon: 'google', fl: true, color: 'orange' })
    return <UiSaveButtons
      others={btns}
      color={appViewerColors.dmv}
    />
  }

  const topper = () => <div className={'topper-flex'}>
    {_.startCase(uivi_direct)}
  </div>

  const header = () => <div className={'header-flex head'}>
    {dataOptionsButton_top()}
    <DataManagementTop />
  </div>

  const footer = () => {
    switch (dataManagementType) {
      case _defaultMenuItem:
        return <div className={'header-flex foot'}>
          {dataOptionsButton_bottom()}
        </div>
      default:
        return null
    }
  }

  const tableViewer = () => {
    return <TransitionWrapper key={uivi_direct} transitionWrapperType={transitionWrapperTypes.fade}>
      {/* <TreeViewer data={_jsonData} scNames={true} subNames={true} /> */}
      <TableViewer
        handleRowSelect={handleRowSelect}
        isAppData={true}
        jsonData={_appData}
        tableRef={tableRef}
        usePropAdjustments={true}
        viewItem={_viewItem}
        showItemKey={true}
      />
    </TransitionWrapper>
  }

  const alphaTableViewer = () => {
    return <AlphaTableViewer appData={_appData} handleRowSelect={handleRowSelect} tableRef={tableRef} viewItem={_viewItem} showItemKey={true} />
  }
  const content_table = () => showAlpha ? alphaTableViewer() : tableViewer()

  /**
   * 
   * @returns AppDataContent bases on the `dataManagementType`
   */
  const content_dataManagementItem = () => <Suspense fallback={<SuspenseDimmer origin={'DataManagementViewer'} />}>
    <Wrapper
      content={<AppDataContent />}
    />
  </Suspense>

  /**
   * 
   * @returns dataContent OR dataManagementContent
   */
  const content_data = () => {
    switch (dataManagementType) {
      case _defaultMenuItem:
        return content_table()
      default:
        return content_dataManagementItem()
    }
  }

  /**
   * 
   * @returns the content in a wrapper
   */
  const wrapper_content = () => {
    let cn = 'wrp-sub'
    if (selectedItem) { cn += ' selected' }
    if (showHelp) {
      return <Help helpType={helpTypes.actions} handleCancel={handleShow_help} />
    } else {
      return _viewItem ?
        <Wrapper
          topper={topper()}
          header={fullMode ? header() : header()}
          content={content_data()}
          footer={fullMode ? footer() : footer()}
          wrapperType={wrapperTypes.paddedFooter}
          updating={updating}
          confirmation={confirmation}
          css={{ container: cn }}
        />
        :
        <Segment basic>No View</Segment>
    }
  }

  const sidebar_topLeft = (visible) => <DataSidebar width={'null'} direction={'left'} visible={visible} content={visible && dataManagementMenu()} onHide={handleShow_dataOptions} />
  const sidebar_right = (visible) => <DataSidebar direction={'right'} visible={visible} content={visible && wrapper_sidebarRight()} onHide={handleViewDataMode} />
  const sidebar_export = (visible) => <DataSidebar direction={'right'} visible={visible} content={visible && wrapper_exportRight()} onHide={handleShowExport} />
  const sidebar_actions = (visible) => <DataSidebar direction={'left'} visible={visible} content={visible && dataSidebarMenu()} onHide={handleShow_dataActions} />

  /**
   * 
   * @returns a Sidebar containing both a left and right sidebar
   */
  const sidebar = () => <Sidebar.Pushable style={{ overflow: 'hidden' }}>
    {sidebar_topLeft(showDataOptions ? true : false)}
    {sidebar_right(viewDataMode ? true : false)}
    {sidebar_export(showExport)}
    {sidebar_actions(showDataActions)}
    <Sidebar.Pusher dimmed={viewDataMode ? true : false} className='h100' >
      {wrapper_content()}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const fullPageWrapper = () => <FullPageWrapper
    content={sidebar()}
    handleCancel={handleDataActionSelect}
    topperCaption={'Data Management'}
    topperCaption2={dataCount ? _.startCase(_uivi) + '(' + dataCount + ')' : _.startCase(_uivi)}
  />

  return _viewItem ? <DataManagementContext.Provider value={{ dataManagement_state, dataManagement_handlers }}>
    <DataProvider uivi={_uivi} clickOriginType={clickOriginType}>
      {_fromDataViewer ? sidebar() : fullPageWrapper()}
    </DataProvider>
  </DataManagementContext.Provider>
    :
    <div>THIS MAY NOT BE NEEDED</div>
}

export default DataManagementViewer